import { Routes } from '@angular/router';
import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { AgentPageComponent } from './pages/agent-page/agent-page.component';
import { GeneralAgentService } from './services/general-agent.service';
import { THREAD_AGENT_SERVICE } from './tokens/thread-agent.token';
import { THREAD_AGENT_CONFIG, GENERAL_AGENT_THREAD_CONFIG } from './tokens/thread-agent-config.token';

/**
 * Route Guard: Redirects to correct component based on query params
 * 
 * - No threadId? → Stay on new-chat route
 * - Has threadId? → Navigate to thread route
 */
const threadGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
    const router = inject(Router);
    const threadId = route.queryParamMap.get('threadId');
    const threadMetadataId = route.queryParamMap.get('threadMetadataId');

    if (threadId && threadMetadataId) {
        // Has thread params → Navigate to thread route
        return router.createUrlTree(['/bob/thread'], {
            queryParams: { threadId, threadMetadataId }
        });
    }

    // No thread → Stay on empty route
    return true;
};

/**
 * Route Guard: Redirect to new-chat if no threadId
 */
const requireThreadGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
    const router = inject(Router);
    const threadId = route.queryParamMap.get('threadId');
    const threadMetadataId = route.queryParamMap.get('threadMetadataId');

    if (!threadId || !threadMetadataId) {
        // No thread params → Redirect to new chat
        return router.createUrlTree(['/bob']);
    }

    // Has thread → Allow access
    return true;
};

/**
 * General Agent Feature Routes
 * 
 * Clean, separated route structure:
 * 
 * /bob                                        → New chat (empty state)
 * /bob/thread?threadId=123&threadMetadataId=456 → Active thread component
 * 
 * Route guards handle navigation logic automatically.
 */
export const GENERAL_AGENT_ROUTES: Routes = [
    {
        path: '',
        component: AgentPageComponent,
        data: { breadcrumb: 'Ask Bob' },
        providers: [
            { provide: THREAD_AGENT_SERVICE, useExisting: GeneralAgentService },
            { provide: THREAD_AGENT_CONFIG, useValue: GENERAL_AGENT_THREAD_CONFIG },
        ],
        children: [
            {
                path: '',
                loadComponent: () => import('./pages/new-chat/new-chat.component')
                    .then(m => m.NewChatComponent),
                canActivate: [threadGuard],
                data: { breadcrumb: '' }
            },
            {
                path: 'thread',
                loadComponent: () => import('./pages/active-thread/active-thread.component')
                    .then(m => m.ActiveThreadComponent),
                canActivate: [requireThreadGuard],
                data: { breadcrumb: 'Thread' }
            },
            {
                path: 'callback/:connectorToolkit',
                loadComponent: () => import('./pages/new-chat/new-chat.component')
                    .then(m => m.NewChatComponent),
                data: { breadcrumb: '' }
            },
            {
                /** Prompt Library screen — view, edit, delete, and run saved prompts. */
                path: 'prompt-library',
                loadComponent: () => import('./pages/prompt-library/prompt-library.component')
                    .then(m => m.PromptLibraryComponent),
                data: { breadcrumb: 'Prompt Library' }
            }
        ]
    }
];
